/**
 * WxIcon primitive (Plan 01-16, Task 3).
 *
 * Loads a Met.no weather glyph from `apps/web/public/weather-icons/<code>.svg`
 * (Plan 07 ships the bundled icon set; CSP `img-src 'self'` from Plan 09
 * blocks any external fetch).
 *
 * Code sanitisation defends against path-traversal attempts in case a
 * raw API string ever bypassed the closed Met.no symbol_code allowlist
 * upstream — only `[a-z0-9_]` survives.
 *
 * onError fallback renders a paper-toned circle (`<span>`) so a missing
 * icon never shows the browser broken-image glyph on the kitchen tablet.
 *
 * Codes supported (UI-SPEC §Component Inventory + plan acceptance):
 *   clearsky_day, clearsky_night, fair_day, fair_night, partlycloudy_day,
 *   partlycloudy_night, cloudy, rain, lightrain, heavyrain, snow,
 *   lightsnow, sleet, fog, thunder.
 *
 * Plan 14's WeatherTile imports this primitive; the testid format is
 * `wx-<code>` per Plan 16 acceptance criteria.
 */
import { useState } from 'react';

interface WxIconProps {
  code: string;
  size?: number;
  className?: string;
}

export function WxIcon({ code, size = 64, className }: WxIconProps) {
  const safe = code.replace(/[^a-z0-9_]/gi, '');
  const [errored, setErrored] = useState(false);

  if (errored) {
    return (
      <span
        data-testid={`wx-fallback-${safe}`}
        role="img"
        aria-label={safe || code}
        className={['inline-block rounded-full bg-inset', className]
          .filter(Boolean)
          .join(' ')}
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <img
      src={`/weather-icons/${safe}.svg`}
      alt={safe || code}
      width={size}
      height={size}
      className={className}
      data-testid={`wx-${safe}`}
      onError={() => setErrored(true)}
    />
  );
}
